import { type UserCredentialsCode } from '@capsicum/common';

export type UseCredentialsHanderProps = {
	credentialCodes: Array<UserCredentialsCode> | UserCredentialsCode;
	onCredentialGranted: () => void;
	page: string;
};

export const enum UserCredentialsStatus {
	DENIED = 'denied',
	DISABLED = 'disabled',
	GRANTED = 'granted'
}

export const enum UserCredentialsDeniedReason {
	// Given when credential is disabled
	CREDENTIAL_IS_DISABLED = 'b2739cfc-4aa6-468b-8fa3-14dacc67c338',

	// Given when credential is not granted based on NCW status
	NCW_STATUS_DENIED = '990f28df-6853-489c-94af-13e3fec363e3',

	NO_GRANT_COUNTRY_CODE = '418bc4df-c2e6-4adc-803a-04c0cb2541ea',

	NO_GRANT_COUNTRY_STATE_CODE = '01c5210f-4456-4fc4-ac27-e4ee1a3610f2',

	// Given when there is no credential rule that grants the credential
	NO_GRANT_NO_RULE = '733cc657-212f-4187-a991-457e38f60332',

	// Given when credential is not granted because user is an official test account
	NO_GRANT_OFFICIAL_TEST_ACCOUNT = '863ad3a5-b951-43cb-8334-a9eeb839d69a',

	// Given when credential is not granted because user has to accept the user agreement
	NO_GRANT_USER_AGREEMENT = '6f3a857c-562f-487a-87af-40890982fc57',

	NO_GRANT_USER_ID = '9f798bd8-9ae7-498d-894d-e345ca5bb981',

	NO_GRANT_USER_REGULATION = 'd575187f-ef71-4cbf-a408-b4afc74faad9',

	// Given when credential is not granted because user requirement state has action deny
	NO_GRANT_USER_REQUIREMENT_DENIED = '8edb2646-99ad-4eb8-bd90-93b20ea5d813',

	// Given when credential is not granted because user requirement state has status not completed
	NO_GRANT_USER_REQUIREMENT_NOT_COMPLETED = '6137bceb-7a2a-4099-8cf2-88a370cd0d56',

	// Partial grant codes
	NO_GRANT_USER_STATUS = 'dba1fb7e-436a-45f2-8064-de586134b2ea',

	// Given when credential is not granted because user needs to finish travel rule
	TRAVEL_RULE_DENIED = '7d89657a-20ee-4820-b835-496812caa049',

	USER_COUNTRY_DENIED = '4294d1f9-ae12-47f8-8d32-97d264dc033f',

	USER_COUNTRY_STATE_DENIED = '48fc7bf7-d824-47f5-a66e-4a70ab4797b5',

	USER_ID_DENIED = '02bd11ad-773b-4c4c-95fa-a35d16623fd9',

	// Given when credential is not granted because user ip country is denied
	USER_IP_COUNTRY_DENIED = '5871b9b9-c48b-4f17-8bcc-95d9b94bf735',

	// Given when credential is not granted because user ip country risk is denied
	USER_IP_COUNTRY_RISK_DENIED = '4735ded7-d2e8-4efc-86f7-c7bffc948bf6',

	// Given when credential is not granted because user ip state is denied
	USER_IP_STATE_DENIED = '8aec6527-8c4d-47b0-bccf-6abcface6a64',
	// Currently being used only for the login credential
	USER_LOCATION_DENIED = 'f83f5f33-fa45-43e5-9d0c-4f21af84aa29',
	USER_REGULATION_DENIED = '3bbbe1a4-4745-4a8f-97ae-97f3cf321108',
	// Deny rule codes
	USER_STATUS_DENIED = '0650da32-2db5-4f97-bc3b-ddd55b74ce65'
}

export const GENERIC_HANDLER_DENIED_REASONS = [
	UserCredentialsDeniedReason.CREDENTIAL_IS_DISABLED,
	UserCredentialsDeniedReason.NO_GRANT_NO_RULE,
	UserCredentialsDeniedReason.NO_GRANT_OFFICIAL_TEST_ACCOUNT,
	UserCredentialsDeniedReason.NO_GRANT_USER_AGREEMENT,
	UserCredentialsDeniedReason.NCW_STATUS_DENIED,
	UserCredentialsDeniedReason.TRAVEL_RULE_DENIED
];

export const REQUIREMENT_NOT_COMPLETED_HANDLER_DENIED_REASONS = [
	UserCredentialsDeniedReason.NO_GRANT_USER_REQUIREMENT_NOT_COMPLETED
];

export const REQUIREMENT_DENIED_HANDLER_DENIED_REASONS = [UserCredentialsDeniedReason.NO_GRANT_USER_REQUIREMENT_DENIED];

export const LOCATION_HANDLER_DENIED_REASONS = [
	UserCredentialsDeniedReason.USER_COUNTRY_DENIED,
	UserCredentialsDeniedReason.USER_COUNTRY_STATE_DENIED,
	UserCredentialsDeniedReason.NO_GRANT_COUNTRY_CODE,
	UserCredentialsDeniedReason.NO_GRANT_COUNTRY_STATE_CODE,
	UserCredentialsDeniedReason.USER_REGULATION_DENIED,
	UserCredentialsDeniedReason.NO_GRANT_USER_REGULATION,
	UserCredentialsDeniedReason.USER_LOCATION_DENIED,
	UserCredentialsDeniedReason.USER_IP_COUNTRY_DENIED,
	UserCredentialsDeniedReason.USER_IP_COUNTRY_RISK_DENIED,
	UserCredentialsDeniedReason.USER_IP_STATE_DENIED
];

export const USER_HANDLER_DENIED_REASONS = [
	UserCredentialsDeniedReason.USER_ID_DENIED,
	UserCredentialsDeniedReason.USER_STATUS_DENIED,
	UserCredentialsDeniedReason.NO_GRANT_USER_ID,
	UserCredentialsDeniedReason.NO_GRANT_USER_STATUS
];

export type CredentialHandlerDeniedReason =
	| 'GENERIC_DENIED_REASON'
	| 'REQUIREMENT_DENIED_REASON'
	| 'REQUIREMENT_NOT_COMPLETED_REASON'
	| undefined;
